import React, { useRef } from 'react';
import { List } from 'immutable';
import classNames from 'classnames';
import { UncontrolledPopover, PopoverBody } from 'reactstrap';
import { Icon } from '../Icon';
import { parseRenderOptions } from './utils';
import { FieldWrapper } from './FieldWrapper';

/******************************************************************************
 * Single Field
 ******************************************************************************/

export const CheckboxComponent = ({ renderOptions, ...props }) => {
  const labelRef = useRef();
  const showHelpTextPopover =
    !!props.helpText && !props.renderAttributes.get('inlineHelpText');

  return (
    <FieldWrapper
      {...props}
      renderOptions={renderOptions}
      helpText={showHelpTextPopover ? null : props.helpText}
    >
      <div
        className={classNames('form-check', renderOptions.fieldClassName, {
          'form-switch': renderOptions.custom.switch,
          'form-check-inline': renderOptions.custom.inline,
          'form-check-sm': renderOptions.size === 'sm',
          'is-invalid': renderOptions.hasErrors,
        })}
        aria-required={props.required}
      >
        <input
          ref={props.focusRef}
          className={classNames('form-check-input')}
          disabled={!props.enabled}
          type="checkbox"
          id={props.id}
          name={props.name}
          checked={!!props.value}
          onBlur={props.onBlur}
          onChange={props.onChange}
          onFocus={props.onFocus}
          form={props.form}
          title={renderOptions.custom.omitOptionLabel ? props.label : undefined}
        />
        {!renderOptions.custom.omitOptionLabel && (
          <label
            className={classNames('form-check-label', {
              required: props.required,
            })}
            htmlFor={props.id}
          >
            {props.label}
          </label>
        )}{' '}
        {showHelpTextPopover && (
          <>
            <span
              className="form-helper-popover"
              ref={labelRef}
              id={`${props.id}-helpTarget`}
            >
              <Icon name="help-circle" className="text-medium" md />
            </span>
            <UncontrolledPopover
              placement="top"
              trigger="legacy"
              target={labelRef}
            >
              <PopoverBody>{props.helpText}</PopoverBody>
            </UncontrolledPopover>
          </>
        )}
      </div>
    </FieldWrapper>
  );
};

CheckboxComponent.defaultProps = {
  errors: List(),
};

export const CheckboxField = props => {
  const renderOptions = parseRenderOptions(props, {
    hasLabel: false,
    isFloating: false,
  });
  return <CheckboxComponent renderOptions={renderOptions} {...props} />;
};

export const CheckboxFilter = props => {
  const renderOptions = parseRenderOptions(
    props,
    {
      hasLabel: false,
      isFloating: false,
    },
    {
      size: 'sm',
    },
  );
  return <CheckboxComponent renderOptions={renderOptions} {...props} />;
};

/******************************************************************************
 * Multi Field
 ******************************************************************************/

export const CheckboxMultiComponent = ({ renderOptions, ...props }) => {
  return (
    <FieldWrapper {...props} renderOptions={renderOptions} fieldset>
      {props.options.map((option, i) => (
        <div
          className={classNames('form-check', renderOptions.fieldClassName, {
            'form-check-sm': renderOptions.size === 'sm',
            'form-check-inline': !props.renderAttributes.get('vertical'),
          })}
          aria-required={props.required}
          key={i}
        >
          <input
            ref={i === 0 ? props.focusRef : null}
            className={classNames('form-check-input')}
            disabled={!props.enabled}
            type="checkbox"
            id={`${props.id}-${option.get('value')}`}
            name={props.name}
            value={option.get('value')}
            checked={props.value.includes(option.get('value'))}
            onChange={props.onChange}
            onBlur={props.onBlur}
            onFocus={props.onFocus}
            form={props.form}
          />
          <label
            className={classNames('form-check-label')}
            htmlFor={`${props.id}-${option.get('value')}`}
          >
            {option.get('label')}
          </label>
        </div>
      ))}
    </FieldWrapper>
  );
};

export const CheckboxMultiField = props => {
  const renderOptions = parseRenderOptions(
    props,
    { isFloating: false },
    { wrapperClassName: 'form-group--check' },
  );
  return <CheckboxMultiComponent renderOptions={renderOptions} {...props} />;
};
